
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */



const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<h1>{`Objects`}</h1>
<p>{`Use literal syntax for object creation.`}</p>
<pre><code parentName="pre" {...{}}>{`// Bad
const object = new Object();

// Good
const object = {};
`}</code></pre>
<p>{`When adding functions to an object, use arrow functions instead of object method shorthand.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? Because arrow functions don't have a `}<inlineCode parentName="p">{`this`}</inlineCode>{` reference. That would prevent you from using it and encountering difficult to track bugs when the objects are passed around potentially changing where `}<inlineCode parentName="p">{`this`}</inlineCode>{` is pointing at.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
const object = {
  prop: 1,
  action() {
    console.log(\`Clicked and \${this.prop}\`);
  },
};

document.addEventListener('click', object.action);  // After click, would print Clicked and undefined

// Good
const object = {
  prop: 1,
  action: () => {
    console.log(\`Clicked and \${object.prop}\`); 
  },
};

document.addEventListener('click', object.action);  // After click, would print Clicked and 1
`}</code></pre>
<p>{`Always use property value shorthand.`}</p>
<pre><code parentName="pre" {...{}}>{`const name = 'User';

// Bad
const object = { name: name };

// Good
const object = { name };
`}</code></pre>
<p>{`Always place shorthand properties at the beginning of an object declaration.`}</p>
<pre><code parentName="pre" {...{}}>{`const name = 'User';

// Bad
const object = {
  lastName: 'Userface',
  name,
};

// Good
const object = {
  name,
  lastName: 'Userface',
};
`}</code></pre>
<p>{`Only quote property names when those are invalid identifiers.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? As we can see in the example, it's easier to differentiate the key from the value when using syntax highlighting.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
const object = { 'name': 'User' };

// Good
const object = { name: 'User', 'last-name': 'Userface' };
`}</code></pre>
<p>{`Prefer object spread operator over `}<inlineCode parentName="p">{`Object.assign`}</inlineCode>{`.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? It can lead to unexpected bugs mutating the original object. Also, it's less concise and difficult to read.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
const original = { name: 'User', age: 20 };
const copy = Object.assign(original, { name: 'User2', lastName: 'Userface' });  // in a slip, we mutated the original object
console.log(copy);  // prints { name: 'User2', lastName: 'Userface', age: 20 }
console.log(original);  // prints { name: 'User2', lastName: 'Userface', age: 20 }

// Good
const original = { name: 'User', age: 20 };
const copy = { ...original, name: 'User2', lastName: 'Userface' };
console.log(copy);  // prints { name: 'User2', lastName: 'Userface', age: 20 }
console.log(original);  // prints { name: 'User', age: 20 }
`}</code></pre>
<p>{`Always use object destructuring when extracting properties from an object.`}</p>
<pre><code parentName="pre" {...{}}>{`const object = { name: 'User', age: 20 };

// Bad
const name = object.name;
const age = object.age;

// Good
const { name, age } = object;
`}</code></pre>
<p>{`Always destructure all the properties from the same object in the same declaration.`}</p>
<pre><code parentName="pre" {...{}}>{`const object = { name: 'User', age: 20 };

// Bad
const { name } = object;
const { age } = object;

// Good
const { name, age } = object;
`}</code></pre>
<p>{`Always use dot notation when accessing properties if possible.`}</p>
<pre><code parentName="pre" {...{}}>{`const object = { name: 'User' };

// Bad
object['name'];

// Good
object.name;
`}</code></pre>
<p>{`Use bracket notation `}<inlineCode parentName="p">{`[]`}</inlineCode>{` when accessing properties with a variable.`}</p>
<pre><code parentName="pre" {...{}}>{`const propName = 'name';
const object = { name: 'User' };
object[propName];
`}</code></pre>
<p>{`If available, use the `}<inlineCode parentName="p">{`?.`}</inlineCode>{` (optional chaining) operator when accesing potentially `}<inlineCode parentName="p">{`null`}</inlineCode>{` or `}<inlineCode parentName="p">{`undefined`}</inlineCode>{` objects instead of testing for existence.`}</p>
<blockquote>
<p parentName="blockquote">{`Even though is only at Stage 1, it's already well supported in Babel`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`const object = {};

// Bad
if (object.user != null) {
  console.log(object.user.name);
}

// Good
console.log(object.user?.name);
`}</code></pre>
<h5><strong parentName="h5">{`NOTE`}</strong>{`: Don't overuse the `}<inlineCode parentName="h5">{`?.`}</inlineCode>{` operator. If a property must be present, don't test for it's inexistence, let the system notify you of unexpected data by failing early.`}</h5>
<p>{`Use computed property names when necessary.`}</p>
<pre><code parentName="pre" {...{}}>{`const foo = 'property';
const object = { [foo]: 'bar' };
console.log(object);  // prints { property: 'bar' };
`}</code></pre>
<p>{`When having to mutate an object, always favor returning a copy of the object instead.`}</p>
<pre><code parentName="pre" {...{}}>{`const object = { name: 'User', lastName: 'Userface' };

// Bad
object.name = 'User2';

// Good
const newObject = { ...object, name: 'User2' };
`}</code></pre>
<p>{`Always use `}<inlineCode parentName="p">{`Object.keys(object)`}</inlineCode>{` over a `}<inlineCode parentName="p">{`for`}</inlineCode>{` loop.`}</p>
<blockquote>
<p parentName="blockquote">{`Why? `}<inlineCode parentName="p">{`for`}</inlineCode>{` loop iterates over all properties of the object and the prototype chain while `}<inlineCode parentName="p">{`Object.keys()`}</inlineCode>{` only iterates over the object own properties.`}</p>
</blockquote>
<pre><code parentName="pre" {...{}}>{`// Bad
for (let key in object) {
}

// Good
Object.keys(object).forEach((key) => {
});
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;